import { IntlShape } from 'react-intl';
import { useApplicationLink } from '~/jobiqo/components/Job/Detail/hooks/useApplicationLink';
import { getJobDimensions } from '~/jobiqo/components/Job/utils/job_analytics';
import { getCurrentFrontendConfig } from '~/jobiqo/context/frontendConfig';
import {
  analyticsDispatch,
  EVENTS,
  PAGE_TYPES,
  PageType,
} from '~/jobiqo/lib/analytics';
import { User } from '~/jobiqo/models/user';
import { AirTrackingService } from '~/jobiqo/services/airTracking';
import { Job } from '~/jobiqo/types/types';
import { parseUniversalDate } from '../utils/dates';
import { isAnonymous } from '../lib/auth';
import {
  buildRedirectUrlWithDestination,
  sanitizeRedirectUrl,
} from '../lib/redirect';
import Router from 'next/router';
import { JobDate } from '~/vcl/models/job';

const getJobsConfig = () => getCurrentFrontendConfig().JOBS || {};

export const getJobOccupation = (job: Job): string => {
  return job.occupationalField
    ? job.occupationalField
        .map((itemTerm) => (itemTerm ? itemTerm.label : null))
        .join(' | ')
    : '';
};

/**
 * Extracts field labels.
 *
 * @param jobField
 *  The job field to extract labels.
 * @returns {[string]}
 *  Array of field labels.
 */
export const getJobFieldLabels = (jobField): string[] =>
  jobField?.map((field) => field?.label);

export const getAddress = (job: Job): string => {
  return job.address ? job.address.join(' | ') : '';
};

/**
 * Extracts job address as a string.
 * Checks HIDE_JOB_LOCATION configuration.
 *
 * @param {Job} job
 *  The job.
 * @returns {string[]}
 *  The appropriate locations array for job location, or empty array
 *  if HIDE_JOB_LOCATION is enabled.
 */
export const getJobLocationToRender = (job: Job): string[] =>
  getJobsConfig().HIDE_JOB_LOCATION || !Boolean(job.address)
    ? []
    : (job.address as string[]);

/**
 * Returns job teaser formatted dates for given job.
 *
 * Checks configuration and isPrivate flag to determine whether
 * the published and/or expiration date should be shown.
 *
 * @param {Job} job
 *  The job.
 * @param {IntlShape} intl
 *  IntlShape used for tranlsations.
 * @param {boolean} isPrivate
 *  The flag indicates if job dates are shown publicly or privately.
 * @param {boolean} isTeaser
 *  The flag indicates if job dates are shown in a teaser.
 * @returns {JobDate[]}
 *  Appropriate job teaser dates if they exists.
 */
export const getJobDatesToRender = (
  job: Job,
  intl: IntlShape,
  isPrivate = false,
  isTeaser = false,
): JobDate[] => {
  const jobsConfig = getJobsConfig();
  const dates: JobDate[] = [];
  const notAvailableMessage = intl.formatMessage({
    id: 'n-a',
    defaultMessage: 'n/a',
  });
  const publishedMessage = intl.formatMessage({
    id: 'job_teaser_published_date',
    defaultMessage: 'Published',
  });
  const expiresMessage = intl.formatMessage({
    id: 'job_teaser_expiration_date',
    defaultMessage: 'Expires',
  });
  const pauseMessage = intl.formatMessage({
    id: 'job_teaser_pause_date',
    defaultMessage: 'Pause',
  });
  const startDateMessage = intl.formatMessage({
    id: 'job_teaser_start_date',
    defaultMessage: 'Start date:',
  });
  const endDateMessage = intl.formatMessage({
    id: 'job_teaser_end_date',
    defaultMessage: 'End date:',
  });
  const startMessage = intl.formatMessage({
    id: 'job_teaser_start',
    defaultMessage: 'Start:',
  });
  const endMessage = intl.formatMessage({
    id: 'job_teaser_end',
    defaultMessage: 'End:',
  });
  // We only want to display the start/end date for the seasonal jobs, no other dates, in order to avoid confusion.
  const isSeasonalJob =
    !!job['availabilityPeriod'] &&
    !!(
      job['availabilityPeriod']['startDate'] &&
      job['availabilityPeriod']['endDate']
    );

  if (
    (jobsConfig.SHOW_PUBLISHED_DATE || jobsConfig.SHOW_EXPIRATION_DATE) &&
    isPrivate
  ) {
    // Get the formatted published date.
    const publishedDateText = job.originalPublished
      ? job.formattedOriginalPublished ??
        formatJobDate(job.originalPublished, intl)
      : notAvailableMessage;

    let endDateText;
    let title;

    // Determine the second date and title based on 'job.pause'. If a job pause
    // date is set, show it instead of the expiration date.
    if (job.pause) {
      endDateText = job.pause
        ? job.formattedPause ?? formatJobDate(job.pause, intl)
        : notAvailableMessage;
      title = `${publishedMessage} - ${pauseMessage}`;
    } else {
      endDateText = job.expiration
        ? job.formattedExpiration ?? formatJobDate(job.expiration, intl)
        : notAvailableMessage;
      title = `${publishedMessage} - ${expiresMessage}`;
    }

    // Push the result and return.
    dates.push({
      text: `${publishedDateText} - ${endDateText}`,
      title: title,
    });

    return dates;
  }

  if (jobsConfig.SHOW_PUBLISHED_DATE && job.published && !isSeasonalJob) {
    dates.push({
      text:
        job.formattedPublished ??
        (formatJobDate(job.published, intl) as string),
      title: publishedMessage,
    });
  }

  if (jobsConfig.SHOW_EXPIRATION_DATE && job.expiration && !isSeasonalJob) {
    // If a job pause date is set, show it instead of the expiration date.
    if (job.pause) {
      dates.push({
        text: job.formattedPause ?? (formatJobDate(job.pause, intl) as string),
        title: pauseMessage,
      });
    } else {
      dates.push({
        text:
          job.formattedExpiration ??
          (formatJobDate(job.expiration, intl) as string),
        title: expiresMessage,
      });
    }
  }

  if (jobsConfig.SEASONAL_JOBS_ENABLED && isSeasonalJob) {
    // For teasers show dates on separate lines.
    if (isTeaser) {
      const startDateStr = job['availabilityPeriod']['startDate']
        ? formatJobDate(job['availabilityPeriod']['startDate'], intl)
        : notAvailableMessage;
      const endDateStr = job['availabilityPeriod']['endDate']
        ? formatJobDate(job['availabilityPeriod']['endDate'], intl)
        : notAvailableMessage;

      dates.push({
        text: `${startMessage} ${startDateStr} ${endMessage} ${endDateStr}`,
        title: `${startDateMessage} - ${endDateMessage}`,
      });
    } else {
      dates.push({
        text: `${startDateMessage} ${
          job['availabilityPeriod']['startDate']
            ? formatJobDate(job['availabilityPeriod']['startDate'], intl)
            : notAvailableMessage
        } - ${endDateMessage} ${
          job['availabilityPeriod']['endDate']
            ? formatJobDate(job['availabilityPeriod']['endDate'], intl)
            : notAvailableMessage
        }`,
        title: `${startDateMessage} - ${endDateMessage}`,
      });
    }

    return dates;
  }

  return dates;
};

/**
 * Checks if the job is iframe.
 *
 * @param {Job} job
 *  The job.
 * @returns
 *  True if job is iframe, false otherwise.
 */
export const isJobIframe = (job: Job): boolean =>
  job.type !== 'job_per_template' &&
  ((job.type === 'job_per_link' &&
    !!job.linkUrl &&
    !job.linkUrl.startsWith('http://')) ||
    (job.type === 'job_per_file' &&
      !!job.file &&
      !job.file.startsWith('http://')));

/**
 * Handle job application after user clicks apply.
 *
 * @param {Job} job
 *  The job the user is applying to.
 * @param {User} user
 *  The current user.
 */
export const handleJobApply = (
  job: Job,
  user?: User,
  pageType: PageType = PAGE_TYPES.JOB_DETAIL,
) => {
  const jobsConfig = getJobsConfig();
  AirTrackingService.postApplyEvent(job);
  const isAnonymousUser = isAnonymous(user as User);

  // Log the even that the applicant started application.
  analyticsDispatch(
    EVENTS.JOB_APPLY_START,
    getJobDimensions(job, user, pageType),
  );

  let applicationLink = useApplicationLink(job);

  if (
    jobsConfig.JOB_APPLICATION_FORCE_REGISTRATION &&
    isAnonymousUser &&
    (job.applicationWorkflow !== 'external' ||
      jobsConfig.JOB_APPLICATION_FORCE_REGISTRATION_ALL_JOBS)
  ) {
    if (job.applicationWorkflow === 'external' && isAnonymousUser) {
      // For external workflow we want the user to be redirected to the page he was in,
      // not the application URL (which is an external site).
      // If he is logged in then we do want him to go to the application URL.
      const currentPage = Router.asPath;
      applicationLink = currentPage;
    }

    applicationLink = sanitizeRedirectUrl(applicationLink, '');
    sessionStorage.setItem('applyForceRegisterLink', applicationLink);

    // Apply destination parameter to redirect the user to the application link
    // after login/registration.
    applicationLink = buildRedirectUrlWithDestination(
      `/user/${jobsConfig.JOB_APPLICATION_FORCE_REGISTRATION_DESTINATION}`,
      applicationLink,
    );
  }

  window.open(applicationLink, '_blank');
};

/**
 * Formats date based on the current locale.
 * Handles issue with timezones, e.g 2030-01-01T00:59:59+01:00
 * is displayed as 2030/01/01.
 * @param date
 * @param {IntlShape} intl
 * @returns
 * Formatted date, with respect to the locale.
 */
export function formatJobDate(date, intl: IntlShape) {
  if (!date) {
    return;
  }
  if (typeof date === 'string' && date.includes('T')) {
    return intl.formatDate(parseUniversalDate(date.split('T')[0]));
  } else {
    if (date === 'string') {
      return intl.formatDate(parseUniversalDate(date));
    }
  }
  return intl.formatDate(date);
}
